import {
	BodyScrollEndEvent,
	ColDef,
	GetRowIdParams,
	RowClassParams,
	RowClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo, useRef, useState } from "react";
import {
	aggridHeaderClass,
	commonCodeCellStyle,
} from "src/components/common/common";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import {
	cellDateComparator,
	onlyTouchGridStyle,
} from "src/utils/grid/gridFunctionUtil";
import {
	defaultColDef,
	getDefaultRowIndexColumnConfig,
	saveGridScrollState,
} from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import {
	defaultKSK1110_R01HeaderDto,
	KSK1110_R01HeaderDto,
} from "./Dto_KSK1110_R01";
import { useKSK1110_R01Store } from "./Store_KSK1110_R01";

const COLUMN_STATE_KEY = "KSK1110_R01-target-grid-column-state";
const HEADER_NAMES: Record<string, string> = {
	rowIndex: "",
	compNm: "사업장",
	transportDt: "날짜",
	inoutClassNm: "입고구분",
	customCd: "거래처",
	customNm: "상호",
	itemCd: "품목코드",
	itemNm: "품명",
	itemSpec: "규격",
	qty: "입고량",
	unit: "단위",
	lotNo: "LotNo",
	cost: "단가",
	inspectStatus: "검사여부",
};

const compareNumbers = (valueA: number | undefined, valueB: number | undefined) =>
	Number(valueA ?? 0) - Number(valueB ?? 0);

interface TableKSK1110R01Props {
	gridRef: React.RefObject<AgGridReact<KSK1110_R01HeaderDto> | null>;
	fetchOne: (target: KSK1110_R01HeaderDto) => Promise<void>;
}

export const Table_KSK1110_R01 = ({
	gridRef,
	fetchOne,
}: TableKSK1110R01Props) => {
	const gridWrapperRef = useRef<HTMLDivElement>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
	const {
		KSK1110_R01List,
		selectedKSK1110_R01,
		initSelectedKSK1110_R01,
		columnState,
		setColumnState,
		setScrollTop,
	} = useKSK1110_R01Store();
	const getCellStyle = useCallback((params: any, key: string) =>
		onlyTouchGridStyle(key, "compNm")(params), []);

	const baseColumnDefs = useMemo<ColDef<KSK1110_R01HeaderDto>[]>(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: "사업장",
			colId: "compNm",
			field: "compNm",
			width: 110,
			cellStyle: (params) => getCellStyle(params, "compNm"),
		},
		{
			headerName: "날짜",
			colId: "transportDt",
			field: "transportDt",
			width: 110,
			valueFormatter: (params) => formatDate(params.value),
			comparator: cellDateComparator,
			cellStyle: (params) => getCellStyle(params, "transportDt"),
		},
		{ headerName: "입고구분", colId: "inoutClassNm", field: "inoutClassNm", width: 110, cellStyle: (params) => getCellStyle(params, "inoutClassNm") },
		{ headerName: "거래처", colId: "customCd", field: "customCd", width: 100, cellStyle: (params) => getCellStyle(params, "customCd") },
		{ headerName: "상호", colId: "customNm", field: "customNm", width: 120, cellStyle: (params) => getCellStyle(params, "customNm") },
		{ headerName: "품목코드", colId: "itemCd", field: "itemCd", width: 120, cellStyle: (params) => getCellStyle(params, "itemCd") },
		{ headerName: "품명", colId: "itemNm", field: "itemNm", width: 140, cellStyle: (params) => getCellStyle(params, "itemNm") },
		{ headerName: "규격", colId: "itemSpec", field: "itemSpec", width: 110, cellStyle: (params) => getCellStyle(params, "itemSpec") },
		{
			headerName: "입고량",
			colId: "qty",
			field: "qty",
			width: 90,
			type: "numericColumn",
			cellStyle: (params) => getCellStyle(params, "qty"),
			valueFormatter: (params) => Number(params.value ?? 0).toLocaleString(),
			comparator: compareNumbers,
		},
		{ headerName: "단위", colId: "unit", field: "unit", width: 70, cellStyle: (params) => getCellStyle(params, "unit") },
		{ headerName: "LotNo", colId: "lotNo", field: "lotNo", width: 130, cellStyle: (params) => getCellStyle(params, "lotNo") },
		{
			headerName: "단가",
			colId: "cost",
			field: "cost",
			width: 90,
			type: "numericColumn",
			cellStyle: (params) => getCellStyle(params, "cost"),
			valueFormatter: (params) => params.node?.rowPinned
				? ""
				: Number(params.value ?? 0).toLocaleString(),
			comparator: compareNumbers,
		},
		{
			headerName: "검사여부",
			colId: "inspectStatus",
			width: 90,
			valueGetter: (params) => params.node?.rowPinned
				? ""
				: params.data?.inspectNo
					? "검사완료"
					: "미검사",
			cellStyle: (params) => ({
				...commonCodeCellStyle,
			}),
		},
	], [getCellStyle]);

	const columnDefs = useMemo<ColDef<KSK1110_R01HeaderDto>[]>(() =>
		GridCustomColumnDefs<KSK1110_R01HeaderDto>({
			baseColumnDefs,
			columnState: columnState?.some((state) => state.colId === "rowIndex")
				? columnState
				: null,
			headerNames: HEADER_NAMES,
			fallbackHeaderClass: aggridHeaderClass,
		}) as ColDef<KSK1110_R01HeaderDto>[], [baseColumnDefs, columnState]);

	const getRowKey = useCallback((row: KSK1110_R01HeaderDto) =>
		`${row.transportNo}|${row.itemNo}`, []);
	const getRowId = useCallback((params: GetRowIdParams<KSK1110_R01HeaderDto>) =>
		getRowKey(params.data), [getRowKey]);

	const pinnedBottomRowData = useMemo<KSK1110_R01HeaderDto[]>(() => [{
		...defaultKSK1110_R01HeaderDto,
		compNm: `${KSK1110_R01List.length.toLocaleString()}건`,
		qty: KSK1110_R01List.reduce((sum, item) => sum + Number(item.qty || 0), 0),
	}], [KSK1110_R01List]);

	const rowClassRules = useMemo<Record<string, (params: RowClassParams<KSK1110_R01HeaderDto>) => boolean>>(() => ({
		selected: (params) => Boolean(
			!params.node.rowPinned
			&& params.data
			&& selectedKSK1110_R01
			&& getRowKey(params.data) === getRowKey(selectedKSK1110_R01),
		),
		hover: (params) => params.node.rowIndex === hoveredRowIndex,
	}), [getRowKey, hoveredRowIndex, selectedKSK1110_R01]);

	const handleRowClick = useCallback((event: RowClickedEvent<KSK1110_R01HeaderDto>) => {
		if (!event.node.rowPinned && event.data) void fetchOne(event.data);
	}, [fetchOne]);

	const handleSelectionClear = useCallback(() => {
		if (selectedKSK1110_R01) {
			void fetchOne(selectedKSK1110_R01);
			return;
		}
		initSelectedKSK1110_R01();
	}, [fetchOne, initSelectedKSK1110_R01, selectedKSK1110_R01]);

	const handleBodyScrollEnd = useCallback((event: BodyScrollEndEvent<KSK1110_R01HeaderDto>) => {
		if (event.direction === "vertical") saveGridScrollState(event.api, setScrollTop);
	}, [setScrollTop]);

	return (
		<GridContextWrapper<KSK1110_R01HeaderDto>
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={KSK1110_R01List}
			initSelected={handleSelectionClear}
			defaultRowData={defaultKSK1110_R01HeaderDto}
			baseColumnDefs={columnDefs}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			enableDataHistory={false}
		>
			<Grid_Touch<KSK1110_R01HeaderDto>
				ref={gridRef}
				rowData={KSK1110_R01List}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={rowClassRules}
				onRowClicked={handleRowClick}
				onBodyScrollEnd={handleBodyScrollEnd}
				pinnedBottomRowData={pinnedBottomRowData}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
			/>
		</GridContextWrapper>
	);
};
